import React, { useEffect, useMemo, useRef } from "react";
import { DateHelper } from "../../../../helpers/DateHelpers";
import { loadFromStorage, removeFromStorage, saveToStorage } from "../../../../helpers/StorageHelpers";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { DateSelectionOutput, DateSelectionSection } from "./DateSelectionSection";
import { MoreOptionsProjectConfig } from "../MoreOptionsFluent9Component";
import { StorageWrapper } from "@src/services/StorageWrapper";
import { LocalStorageKeys } from "../../../../models/StorageKeys";

const ON_DATE_KEY = "on-date";

const EXPIRATION_OPTION_KEYS = [
    { key: "1", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.1_DAY" },
    { key: "2", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.2_DAYS" },
    { key: "3", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.3_DAYS" },
    { key: "4", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.4_DAYS" },
    { key: "5", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.5_DAYS" },
    { key: "6", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.6_DAYS" },
    { key: "7", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.1_WEEK" },
    { key: "14", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.2_WEEKS" },
    { key: "21", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.3_WEEKS" },
    { key: "28", i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.1_MONTH" },
    { key: ON_DATE_KEY, i18nKey: "SHARED.MOREOPTIONS.EXPIRATION_DROPDOWN.ON_DATE" },
];

export function expirationKeyToDays(key: string): number {
    if (key === ON_DATE_KEY) return 0;
    return parseInt(key, 10);
}

function getKeyForDefaultExpireDays(days: number): string {
    const match = EXPIRATION_OPTION_KEYS.find((o) => o.key !== ON_DATE_KEY && expirationKeyToDays(o.key) === days);
    return match ? match.key : ON_DATE_KEY;
}

export interface TransferExpiresSectionProps extends LocalizeContextProps {
    dateSelection: DateSelectionOutput;
    projectConfig: MoreOptionsProjectConfig;
    storageWrapper: StorageWrapper;
    isInactive: boolean;
    onDateSelectionChange?: (output: DateSelectionOutput) => void;
}

export const TransferExpiresSection = withLocalize((props: TransferExpiresSectionProps) => {
    const [savedChecked, setSavedChecked] = React.useState<boolean>(false);
    const [savedSelectedOption, setSavedSelectedOption] = React.useState<string>("");
    const [savedDate, setSavedDate] = React.useState<Date | undefined>(undefined);

    const isChangedRef = useRef(false);

    const expirationOptions = useMemo(
        () =>
            EXPIRATION_OPTION_KEYS.map((opt) => ({
                key: opt.key,
                text: props.translate(opt.i18nKey) as string,
            })),
        [props.translate]
    );

    useEffect(() => {
        const saved = loadFromStorage(props.storageWrapper, LocalStorageKeys.expireDate);
        if (saved) {
            setSavedChecked(true);
            setSavedSelectedOption(saved.key);
            setSavedDate(DateHelper.addDays(DateHelper.TodayDate, Math.ceil(saved.days)));
        } else {
            setSavedChecked(false);
            setSavedSelectedOption("");
            setSavedDate(undefined);
        }
    }, [props.isInactive]);

    useEffect(() => {
        if (props.isInactive) {
            isChangedRef.current = false;
            return;
        }
        if (isChangedRef.current) return;

        if (
            props.projectConfig.transferExpiresByDefault &&
            props.projectConfig.defaultExpireDays &&
            props.projectConfig.defaultExpireDays > 0
        ) {
            props.onDateSelectionChange?.({
                Checked: true,
                SelectedOption: getKeyForDefaultExpireDays(props.projectConfig.defaultExpireDays),
                SelectedDate: DateHelper.addDays(DateHelper.TodayDate, props.projectConfig.defaultExpireDays),
            });
            return;
        }

        if (savedChecked) {
            props.onDateSelectionChange?.({
                Checked: true,
                SelectedOption: savedSelectedOption,
                SelectedDate: savedDate ?? undefined,
            });
            return;
        }
    }, [
        props.isInactive,
        props.projectConfig.transferExpiresByDefault,
        props.projectConfig.defaultExpireDays,
        savedChecked,
        savedSelectedOption,
        savedDate,
    ]);

    const handleDateSelectionChange = (output: DateSelectionOutput) => {
        const outputToSave = { ...output };

        if (output.Checked) {
            if (!output.SelectedOption) {
                const defaultOption = expirationOptions.find((o) => o.key === "14") ?? expirationOptions[0];
                outputToSave.SelectedOption = defaultOption.key;
                outputToSave.SelectedDate = DateHelper.addDays(
                    DateHelper.TodayDate,
                    expirationKeyToDays(defaultOption.key)
                );
            } else if (output.SelectedOption === ON_DATE_KEY) {
                if (!output.SelectedDate || DateHelper.isSameDay(output.SelectedDate, DateHelper.TodayDate)) {
                    outputToSave.SelectedDate = DateHelper.addDays(DateHelper.TodayDate, 1);
                }
            } else {
                outputToSave.SelectedDate = DateHelper.addDays(
                    DateHelper.TodayDate,
                    expirationKeyToDays(output.SelectedOption)
                );
            }

            const option = expirationOptions.find((o) => o.key === outputToSave.SelectedOption);
            if (option) {
                const days =
                    outputToSave.SelectedOption === ON_DATE_KEY
                        ? DateHelper.subDate(DateHelper.TodayDate, outputToSave.SelectedDate!)
                        : expirationKeyToDays(option.key);

                saveToStorage(props.storageWrapper, LocalStorageKeys.expireDate, {
                    key: option.key,
                    text: option.text,
                    days,
                });
            }
        } else removeFromStorage(props.storageWrapper, LocalStorageKeys.expireDate);

        isChangedRef.current = true;
        props.onDateSelectionChange?.(outputToSave);
    };

    return (
        <>
            <DateSelectionSection
                testId="expires-section"
                label={props.translate("SHARED.MOREOPTIONS.CHECKBOX_TRANSFER") as string}
                options={expirationOptions}
                selectedOption={props.dateSelection.SelectedOption}
                optionsPlaceholder={props.translate("SHARED.MOREOPTIONS.DROPDOWN_PLACEHOLDER_EXPIRATION") as string}
                checked={props.dateSelection.Checked}
                selectedDate={props.dateSelection.SelectedDate}
                onChange={handleDateSelectionChange}
            />
        </>
    );
});
